import React, { useEffect, useState } from 'react';
import { portalReceipts } from '../api';

const fmt = (n: number) => '€' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export default function Receipts() {
  const today = new Date().toISOString().split('T')[0];
  const monthStart = today.substring(0, 7) + '-01';
  const user = JSON.parse(localStorage.getItem('portal_user') || '{}');
  const lockerList = (user.locker_codes || '').split(',').map((c: string) => c.trim()).filter(Boolean);

  const [receipts, setReceipts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(monthStart);
  const [toDate, setToDate] = useState(today);
  const [lockerCode, setLockerCode] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params: any = { from_date: fromDate, to_date: toDate, limit: 200 };
      if (lockerCode) params.locker_code = lockerCode;
      const r = await portalReceipts(params);
      setReceipts(r.data.receipts || []);
    } catch {} finally { setLoading(false); }
  };

  const totalGross = receipts.reduce((s, r) => s + r.gross_amount, 0);

  const exportExcel = async () => {
    const XLSX = await import('xlsx');
    const rows = [
      ['Date', 'Locker', 'Mark', 'Gross', 'Net', 'VAT', 'Reference', 'Receipt URL'],
      ...receipts.map(r => [
        r.issued_at.substring(0, 16).replace('T', ' '),
        r.locker_code, r.mark,
        r.gross_amount, r.net_amount, r.vat_amount,
        r.transaction_ref || '', r.receipt_url,
      ]),
    ];
    const ws = (XLSX as any).utils.aoa_to_sheet(rows);
    const wb = (XLSX as any).utils.book_new();
    (XLSX as any).utils.book_append_sheet(wb, ws, 'Receipts');
    (XLSX as any).writeFile(wb, `receipts_${fromDate}_${toDate}.xlsx`);
  };

  const inputStyle = { padding: '7px 10px', borderRadius: 6, border: '0.5px solid #ccc', fontSize: 13 };

  return (
    <div>
      <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 20 }}>Receipts</h2>

      <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>From</label>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>To</label>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
        </div>
        {lockerList.length > 1 && (
          <div>
            <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Locker</label>
            <select value={lockerCode} onChange={e => setLockerCode(e.target.value)} style={inputStyle}>
              <option value="">All lockers</option>
              {lockerList.map((c: string) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        )}
        <button onClick={load} disabled={loading}
          style={{ padding: '8px 20px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
          {loading ? 'Loading...' : 'Search'}
        </button>
        {receipts.length > 0 && (
          <button onClick={exportExcel}
            style={{ padding: '8px 16px', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
            Export Excel
          </button>
        )}
      </div>

      {receipts.length > 0 && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '10px 20px', fontSize: 13 }}>
            <span style={{ color: '#888' }}>Total receipts: </span><strong>{receipts.length}</strong>
          </div>
          <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, padding: '10px 20px', fontSize: 13 }}>
            <span style={{ color: '#888' }}>Total gross: </span><strong>{fmt(totalGross)}</strong>
          </div>
        </div>
      )}

      <div style={{ background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8f9fa', borderBottom: '0.5px solid #e0e0e0' }}>
              {['Date', 'Locker', 'Mark', 'Gross', 'Net', 'VAT', 'Reference', 'Receipt'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500, color: '#555' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>Loading...</td></tr>
            ) : receipts.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: 'center', color: '#888' }}>No receipts found</td></tr>
            ) : receipts.map((r, i) => (
              <tr key={r.id} style={{ borderBottom: '0.5px solid #f0f0f0', background: i % 2 === 0 ? 'white' : '#fafafa' }}>
                <td style={{ padding: '8px 14px', color: '#666', whiteSpace: 'nowrap' }}>{r.issued_at.substring(0, 16).replace('T', ' ')}</td>
                <td style={{ padding: '8px 14px', fontWeight: 500 }}>{r.locker_code}</td>
                <td style={{ padding: '8px 14px', fontFamily: 'monospace', fontSize: 11, color: '#444' }}>{r.mark}</td>
                <td style={{ padding: '8px 14px' }}>{fmt(r.gross_amount)}</td>
                <td style={{ padding: '8px 14px', color: '#666' }}>{fmt(r.net_amount)}</td>
                <td style={{ padding: '8px 14px', color: '#666' }}>{fmt(r.vat_amount)}</td>
                <td style={{ padding: '8px 14px', color: '#888', fontSize: 12 }}>{r.transaction_ref || '—'}</td>
                <td style={{ padding: '8px 14px' }}>
                  <a href={r.receipt_url} target="_blank" rel="noreferrer"
                    style={{ color: '#185FA5', fontSize: 12, textDecoration: 'none', fontWeight: 500 }}>
                    View ↗
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
